import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Minus, Plus, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { useCart } from "@/context/CartContext";
import { api, fmt, WHATSAPP_URL } from "@/lib/api";

export default function ProductPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [qty, setQty] = useState(1);
  const { addItem, setIsOpen } = useCart();

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    setSize(null);
    setColor(null);
    setQty(1);
    api
      .get(`/products/${slug}`)
      .then(({ data }) => setProduct(data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 px-6" data-testid="product-not-found">
        <p className="font-display uppercase text-3xl text-zinc-500">Pièce introuvable</p>
        <Link to="/boutique" className="btn-ghost">Retour à la boutique</Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="product-loading">
        <Loader2 className="w-8 h-8 animate-spin text-ice" />
      </div>
    );
  }

  const addToCart = () => {
    if (!size) {
      toast.error("Choisis une taille d'abord.");
      return;
    }
    if (!color) {
      toast.error("Choisis une couleur d'abord.");
      return;
    }
    addItem(product, size, color, qty);
    toast.success(`${product.name} ajouté au panier.`);
    setIsOpen(true);
  };

  return (
    <div className="pt-24 pb-24" data-testid="product-page">
      <Seo
        title={`${product.name} — S'NO`}
        siteName="S'NO"
        description={product.description}
        image="/logo.webp"
      />

      <div className="px-4 sm:px-6 lg:px-12">
        <Link
          to="/boutique"
          data-testid="product-back-link"
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-zinc-500 hover:text-ice transition-colors mb-10"
        >
          <ArrowLeft className="w-4 h-4" /> Boutique
        </Link>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative border border-white/10 bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.08),transparent_70%)] bg-smoke"
          >
            <div className="aspect-[3/4] overflow-hidden glitch-frame">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-image"
              />
              <img src={product.image} alt="" aria-hidden="true" className="glitch-copy w-full h-full object-cover" />
            </div>
            <span className="absolute top-4 left-4 bg-black/70 backdrop-blur border border-white/10 font-mono text-[10px] uppercase tracking-widest text-ice px-2.5 py-1.5">
              {product.tag}
            </span>
          </motion.div>

          {/* Details */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="font-mono text-xs uppercase tracking-[0.4em] text-ice mb-3">
              {product.gender} / {product.category.replace("-", " ")}
            </p>
            <h1 className="font-display font-black uppercase text-5xl sm:text-6xl leading-[0.9]" data-testid="product-name">
              {product.name}
            </h1>
            <p className="font-mono text-2xl text-ice mt-5" data-testid="product-price">
              {fmt(product.price)}
            </p>
            <p className="text-sm text-zinc-400 leading-relaxed mt-6 max-w-md" data-testid="product-description">
              {product.description}
            </p>

            {/* Colors */}
            <div className="mt-8">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3">
                Couleur {color && <span className="text-white">— {color}</span>}
              </p>
              <div className="flex gap-3" data-testid="product-colors">
                {product.colors.map((c) => (
                  <button
                    key={c.name}
                    data-testid={`color-option-${c.name.toLowerCase().replace(/[^a-z]/g, "")}`}
                    onClick={() => setColor(c.name)}
                    title={c.name}
                    aria-label={c.name}
                    className={`w-9 h-9 rounded-full border-2 transition-all duration-300 ${
                      color === c.name ? "border-ice scale-110 shadow-[0_0_15px_rgba(0,240,255,0.4)]" : "border-white/20 hover:border-white/60"
                    }`}
                    style={{ background: c.hex }}
                  />
                ))}
              </div>
            </div>

            {/* Sizes */}
            <div className="mt-8">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3">Taille</p>
              <div className="flex flex-wrap gap-2" data-testid="product-sizes">
                {product.sizes.map((s) => (
                  <button
                    key={s}
                    data-testid={`size-option-${s.toLowerCase()}`}
                    onClick={() => setSize(s)}
                    className={`min-w-[52px] px-4 py-3 font-mono text-sm border transition-colors duration-300 ${
                      size === s
                        ? "border-ice bg-ice text-black font-bold"
                        : "border-white/15 text-zinc-300 hover:border-white/50"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Qty + add */}
            <div className="mt-10 flex flex-wrap items-stretch gap-3">
              <div className="flex items-center border border-white/15">
                <button
                  data-testid="qty-minus-button"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="p-4 text-zinc-400 hover:text-ice"
                  aria-label="Moins"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-10 text-center font-mono" data-testid="qty-value">{qty}</span>
                <button
                  data-testid="qty-plus-button"
                  onClick={() => setQty((q) => Math.min(20, q + 1))}
                  className="p-4 text-zinc-400 hover:text-ice"
                  aria-label="Plus"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <button onClick={addToCart} className="btn-ice flex-1 min-w-[220px]" data-testid="add-to-cart-button">
                <ShoppingBag className="w-5 h-5" /> Ajouter au panier
              </button>
            </div>

            {/* Specs */}
            <div className="mt-10 border-t border-white/10 pt-8">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-4">
                Specs techniques
              </p>
              <ul className="space-y-2.5" data-testid="product-specs">
                {(product.specs || []).map((s, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-zinc-300">
                    <span className="w-1.5 h-1.5 bg-ice shrink-0" /> {s}
                  </li>
                ))}
              </ul>
            </div>

            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="product-whatsapp-link"
              className="inline-block mt-8 font-mono text-xs uppercase tracking-[0.25em] text-zinc-500 hover:text-[#25D366] transition-colors"
            >
              Une question sur cette pièce ? Écris-nous sur WhatsApp
            </a>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
